import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import Gyanbeej from '../media/LOGO Gyanbeej transparent.png';
import GyanSparkLogoDark from '../media/gyanspark-logo-dark-horizontal.svg';
import heroImg from '../media/generated/hero-home.webp';
import visionImg from '../media/generated/vision.webp';
import learnerImg from '../media/generated/research-learner.webp';
import personalizationImg from '../media/generated/research-personalization.webp';
import loopImg from '../media/generated/approach-loop.webp';
import { Section, SectionHeading, PageHero, Card, IconBadge, Faq, CtaBand, fadeUp } from '../components/ui';
import { ArrowUpRightIcon, ChatIcon, LinkNodeIcon, BrainIcon, SparkleIcon, PuzzleIcon, BookIcon, BoltIcon, CrownIcon } from '../components/icons';

const MissionVision = () => (
  <Section tone="raised">
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp} className="space-y-8">
        <div>
          <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Our mission</span>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-3 mb-4 text-white">
            Make AI in education understand the learner
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            We build AI systems that remember, adapt, and explain in the way each person learns best — so that
            technology meets learners where they are, instead of asking every learner to adapt to the technology.
          </p>
        </div>
        <div>
          <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Our vision</span>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-3 mb-4 text-white">
            Personalized learning as the default, not the privilege
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            A world where every learner has access to teaching that adapts to them — where personalization
            is simply how education technology works, from the first question a student asks to the platforms
            that serve millions.
          </p>
        </div>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
        <img src={visionImg} alt="Illustration of a path of light rising toward a horizon" className="w-full max-w-md mx-auto" />
      </motion.div>
    </div>
  </Section>
);

const CoreWork = () => (
  <Section>
    <SectionHeading
      eyebrow="What we do"
      title="AI that understands how people learn"
      subtitle="At our core, we research and engineer the systems that let software form a genuine, continuous understanding of a learner — and act on it."
    />
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp}>
        <img src={learnerImg} alt="Illustration of a learner profile deepening over time" className="w-full max-w-lg mx-auto" />
      </motion.div>
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { icon: BrainIcon, title: 'Learner understanding', desc: 'Modelling what a person knows, where they struggle, and how they learn — continuously, not per session.' },
          { icon: SparkleIcon, title: 'Personalized explanation', desc: 'Generating explanations shaped to the learner, rather than answers that are merely correct.' },
          { icon: BoltIcon, title: 'Adaptive intervention', desc: 'Knowing when to step in and how — telling a one-off slip apart from a persistent misconception.' },
          { icon: PuzzleIcon, title: 'Outcome measurement', desc: 'Evaluating understanding over weeks and months, not engagement in the moment.' },
        ].map((item) => (
          <Card key={item.title}>
            <IconBadge icon={item.icon} className="mb-3" />
            <h3 className="font-bold text-white mb-1">{item.title}</h3>
            <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
          </Card>
        ))}
      </div>
    </div>
  </Section>
);

const Personalization = () => (
  <Section tone="raised">
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp}>
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Why personalization</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-5 text-white">
          Every learner is different. Most software doesn't notice.
        </h2>
        <p className="text-lg text-gray-400 leading-relaxed mb-4">
          A learner can get the right answer and still not understand the idea. They can struggle with the
          same concept across weeks, across tools, and be treated as a stranger every single time.
        </p>
        <p className="text-lg text-gray-400 leading-relaxed">
          We work on that gap. The same knowledge, shaped differently for different people, delivered by
          systems that carry context forward — that is what we mean by personalization, and it is the thread
          running through everything we build.
        </p>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
        <img src={personalizationImg} alt="Illustration of one beam of light splitting into three distinct shapes" className="w-full max-w-sm mx-auto" />
      </motion.div>
    </div>
  </Section>
);

const Approach = () => (
  <Section>
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp} className="order-2 lg:order-1">
        <img src={loopImg} alt="Illustration of a continuous research, build and learn cycle" className="w-full max-w-sm mx-auto" />
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="order-1 lg:order-2">
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">How we work</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-6 text-white">Research that ships</h2>
        <div className="space-y-5">
          {[
            { title: 'Grounded in learning science', desc: 'We start from how people actually learn — cognitive load, retrieval, misconceptions — not from what a model makes easy to build.' },
            { title: 'Built with real learners', desc: 'Our systems are developed alongside students, educators, and platforms, and tested where learning actually happens.' },
            { title: 'Measured on understanding', desc: 'We judge our work by what a learner understands months later, not by how impressive a single answer looks.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-4">
              <div className="w-1.5 rounded-full bg-gradient-to-b from-purple-500 to-fuchsia-500 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-white mb-1">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </Section>
);

const FromResearchToProducts = () => (
  <Section>
    <SectionHeading
      eyebrow="From research to products"
      title="Our work in the world"
      subtitle="The research becomes products people use every day — one for learners directly, one for the platforms that serve them."
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {[
        {
          logo: GyanSparkLogoDark,
          logoClass: 'h-6',
          icon: LinkNodeIcon,
          tone: 'amber',
          text: 'text-amber-400',
          audience: 'For education platforms',
          desc: 'A personalization layer that gives every AI feature a platform runs one shared understanding of each learner.',
          to: '/products/gyanspark',
          flagship: true,
        },
        {
          logo: Gyanbeej,
          logoClass: 'h-8',
          icon: ChatIcon,
          tone: 'purple',
          text: 'text-purple-400',
          audience: 'For learners, educators & families',
          desc: 'An AI learning assistant that explains, quizzes, and converses — adapting to the student in front of it.',
          to: '/products/gyanbeej',
        },
      ].map((p) => (
        <Card key={p.to} hover className={`relative flex flex-col ${p.flagship ? 'border-amber-400/30' : ''}`}>
          {p.flagship && (
            <span className="absolute -top-3 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500 text-[#1a0033] text-xs font-extrabold uppercase tracking-wide shadow-lg">
              <CrownIcon className="w-3.5 h-3.5" /> Flagship
            </span>
          )}
          <IconBadge icon={p.icon} tone={p.tone} className="mb-5" />
          <span className={`text-xs font-bold tracking-widest uppercase ${p.text}`}>{p.audience}</span>
          <img src={p.logo} alt="" className={`${p.logoClass} object-contain self-start my-4`} />
          <p className="text-sm text-gray-400 leading-relaxed mb-5 flex-1">{p.desc}</p>
          <Link to={p.to} className={`inline-flex items-center gap-1.5 text-sm font-semibold hover:underline ${p.text}`}>
            Learn more <ArrowUpRightIcon className="w-3.5 h-3.5" />
          </Link>
        </Card>
      ))}
    </div>
    <div className="text-center mt-8">
      <Link to="/products" className="text-sm font-semibold text-purple-400 hover:underline">
        Compare both products →
      </Link>
    </div>
  </Section>
);

const WhoBenefits = () => (
  <Section tone="raised">
    <SectionHeading
      eyebrow="Who we build for"
      title="From one learner to an entire platform"
      subtitle="Personalization has to work at every scale — for a single student at a desk, and for the systems serving millions."
    />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
      {[
        { icon: ChatIcon, title: 'Learners', desc: 'Students who need explanations that fit them, practice that targets their gaps, and support that remembers yesterday.' },
        { icon: BookIcon, title: 'Educators', desc: 'Teachers and institutions who want AI that supports how they teach, with insight into how each student is really doing.' },
        { icon: LinkNodeIcon, title: 'Education platforms', desc: 'Product teams who have built AI features and want them to understand each learner, consistently, over time.' },
      ].map((item) => (
        <Card key={item.title} hover>
          <IconBadge icon={item.icon} className="mb-4" />
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </Card>
      ))}
    </div>
  </Section>
);

const HomeFaq = () => (
  <Section>
    <SectionHeading eyebrow="FAQ" title="Frequently asked questions" />
    <Faq
      items={[
        {
          q: 'What does Preative Solutions do?',
          a: 'Preative Solutions is a research-driven AI studio focused on education. We research and build AI systems that bring genuine personalization to learning — understanding each learner and adapting to them — and we ship that work as products: Gyanbeej for learners and GyanSpark for education platforms.',
        },
        {
          q: 'What do you mean by personalization?',
          a: 'Not a name on a dashboard. We mean systems that build a continuous understanding of what a learner knows, where they struggle, and how they learn best — and then shape explanations, practice, and intervention to fit that person, carrying context forward across sessions.',
        },
        {
          q: 'Are you a research company or a product company?',
          a: 'Both, deliberately. Our core work is research into learner understanding, adaptive explanation, and outcome measurement. That research becomes products used by real students and platforms, and what we learn from those products feeds directly back into the research.',
        },
        {
          q: 'Who are your products for?',
          a: 'Gyanbeej is used directly by students, teachers, and parents. GyanSpark is infrastructure for EdTech platforms that already run AI features and want them to share one understanding of each learner.',
        },
        {
          q: 'Where is Preative Solutions based?',
          a: 'We are an India-based startup incubated at SIIC, IIT Kanpur, building education AI for learners in India and beyond.',
        },
        {
          q: 'How can I work with you?',
          a: 'Platforms and institutions can reach us through the contact page. If you want to join the team, see our careers page — we review every application.',
        },
      ]}
    />
  </Section>
);

const Home = () => (
  <>
    <Seo
      rawTitle="Preative Solutions | AI Research & Products for Education"
      description="Preative Solutions is a research-driven AI studio building systems that understand how people learn. We bring genuine personalization to education through Gyanbeej and GyanSpark."
      path="/"
    />
    <PageHero
      eyebrow="AI research & product studio for education"
      title={
        <>
          Building AI that understands{' '}
          <span className="bg-gradient-to-r from-purple-400 to-fuchsia-400 bg-clip-text text-transparent">how people learn</span>
        </>
      }
      subtitle="Preative Solutions researches and builds AI systems that bring genuine personalization to education — so learning adapts to the learner, not the other way around."
      image={heroImg}
      imageAlt="Abstract illustration of a constellation of ideas flowing into an open book"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/about"
          className="px-8 py-3.5 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors text-center"
        >
          What we do
        </Link>
        <Link
          to="/products"
          className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors text-center"
        >
          See our products
        </Link>
      </div>
    </PageHero>
    <FromResearchToProducts />
    <MissionVision />
    <CoreWork />
    <Personalization />
    <Approach />
    <WhoBenefits />
    <HomeFaq />
    <CtaBand title="Want to build with us?" subtitle="We're always looking for people who care about AI and how people learn.">
      <Link to="/careers" className="px-7 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors">
        View careers
      </Link>
      <Link to="/contact" className="px-7 py-3 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-colors">
        Contact us
      </Link>
    </CtaBand>
  </>
);

export default Home;
